import * as React from 'react';
import {
  Body,
  Checkbox,
  Input,
  InputWrapper,
} from 'vet-bones/design-system/components';

import { SecretInput } from 'src/ui/components/SecretInput';
import {
  CONFIGURE_ORG_SSO_DIALOG_CLIENT_ID,
  CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET,
  CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET_REPLACE,
  CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET_VIEW,
  CONFIGURE_ORG_SSO_DIALOG_DISCOVERY_URL,
  CONFIGURE_ORG_SSO_DIALOG_IDP_LOGOUT,
  CONFIGURE_ORG_SSO_DIALOG_OKTA_AUDIENCE,
  CONFIGURE_ORG_SSO_DIALOG_OKTA_BACKEND_CLIENT_ID,
  CONFIGURE_ORG_SSO_DIALOG_OKTA_BACKEND_CLIENT_SECRET,
  CONFIGURE_ORG_SSO_DIALOG_OKTA_BACKEND_CLIENT_SECRET_REPLACE,
  CONFIGURE_ORG_SSO_DIALOG_OKTA_BACKEND_CLIENT_SECRET_VIEW,
} from 'src/ui/constants/testIds';
import { OrganizationOktaSsoConfig } from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';

export interface OktaFormData {
  discoveryUrl: string;
  clientId: string;
  clientSecret: string;
  oktaAuthorizationServerAudience: string;
  oktaBackendClientId: string;
  oktaBackendClientSecret: string;
  idpLogoutEnabled: boolean;
}

export const getInitialOktaFormData = (): OktaFormData => ({
  discoveryUrl: '',
  clientId: '',
  clientSecret: '',
  oktaAuthorizationServerAudience: '',
  oktaBackendClientId: '',
  oktaBackendClientSecret: '',
  idpLogoutEnabled: false,
});

export const populateOktaForm = (
  config: OrganizationOktaSsoConfig
): OktaFormData => ({
  discoveryUrl: config.discoveryUrl,
  clientId: config.clientId,
  clientSecret: '',
  oktaAuthorizationServerAudience: config.oktaAuthorizationServerAudience ?? '',
  oktaBackendClientId: config.oktaBackendClientId ?? '',
  oktaBackendClientSecret: '',
  idpLogoutEnabled: config.idpLogoutEnabled,
});

export const buildOktaCreateInput = (form: OktaFormData) => ({
  discoveryUrl: form.discoveryUrl.trim(),
  clientId: form.clientId.trim(),
  clientSecret: form.clientSecret.trim(),
  oktaAuthorizationServerAudience: form.oktaAuthorizationServerAudience.trim(),
  oktaBackendClientId: form.oktaBackendClientId.trim(),
  oktaBackendClientSecret: form.oktaBackendClientSecret.trim(),
  idpLogoutEnabled: form.idpLogoutEnabled,
});

export const buildOktaUpdateInput = (form: OktaFormData) => {
  const input: Record<string, string | boolean> = {
    discoveryUrl: form.discoveryUrl.trim(),
    clientId: form.clientId.trim(),
    oktaAuthorizationServerAudience: form.oktaAuthorizationServerAudience.trim(),
    oktaBackendClientId: form.oktaBackendClientId.trim(),
    idpLogoutEnabled: form.idpLogoutEnabled,
  };
  if (form.clientSecret.trim()) {
    input.clientSecret = form.clientSecret.trim();
  }
  if (form.oktaBackendClientSecret.trim()) {
    input.oktaBackendClientSecret = form.oktaBackendClientSecret.trim();
  }
  return input;
};

export const isOktaCreateValid = (form: OktaFormData) =>
  form.discoveryUrl.trim() !== '' &&
  form.clientId.trim() !== '' &&
  form.clientSecret.trim() !== '' &&
  form.oktaAuthorizationServerAudience.trim() !== '' &&
  form.oktaBackendClientId.trim() !== '' &&
  form.oktaBackendClientSecret.trim() !== '';

export const isOktaEditValid = (form: OktaFormData) =>
  form.discoveryUrl.trim() !== '' &&
  form.clientId.trim() !== '' &&
  form.oktaAuthorizationServerAudience.trim() !== '' &&
  form.oktaBackendClientId.trim() !== '';

export const isOktaEditDirty = (
  form: OktaFormData,
  config: OrganizationOktaSsoConfig
) =>
  JSON.stringify({
    discoveryUrl: form.discoveryUrl,
    clientId: form.clientId,
    idpLogoutEnabled: form.idpLogoutEnabled,
    oktaAuthorizationServerAudience: form.oktaAuthorizationServerAudience,
    oktaBackendClientId: form.oktaBackendClientId,
  }) !==
    JSON.stringify({
      discoveryUrl: config.discoveryUrl,
      clientId: config.clientId,
      idpLogoutEnabled: config.idpLogoutEnabled,
      oktaAuthorizationServerAudience:
        config.oktaAuthorizationServerAudience ?? '',
      oktaBackendClientId: config.oktaBackendClientId ?? '',
    }) ||
  form.clientSecret !== '' ||
  form.oktaBackendClientSecret !== '';

interface ConfigureOrgSSOOktaFormProps {
  form: OktaFormData;
  isCreate: boolean;
  onChange: (form: OktaFormData) => void;
}

export const ConfigureOrgSSOOktaForm: React.VFC<ConfigureOrgSSOOktaFormProps> = ({
  form,
  isCreate,
  onChange,
}) => (
  <>
    <div className="organization-dialog-container">
      <Body className="organization-dialog-container-header" size="lg">
        {copy.components.configureOrgSSO.dialog.okta.webApp.heading}
      </Body>
      <Body className="organization-dialog-container-header" size="md">
        {copy.components.configureOrgSSO.dialog.okta.webApp.description}
      </Body>
    </div>
    <Input
      className={isCreate ? 'organization-dialog-input-help-icon-hidden' : ''}
      clearable={false}
      helpProps={{
        text: isCreate
          ? copy.components.configureOrgSSO.dialog.okta.webApp.discoveryUrl
              .helpCreate
          : copy.components.configureOrgSSO.dialog.okta.webApp.discoveryUrl
              .helpEdit,
      }}
      label={
        copy.components.configureOrgSSO.dialog.okta.webApp.discoveryUrl.label
      }
      onChange={(e) => onChange({ ...form, discoveryUrl: e.target.value })}
      placeholder={
        copy.components.configureOrgSSO.dialog.okta.webApp.discoveryUrl
          .placeholder
      }
      required
      size="md"
      testId={CONFIGURE_ORG_SSO_DIALOG_DISCOVERY_URL}
      value={form.discoveryUrl}
    />
    <Input
      className="organization-dialog-input-help-icon-hidden"
      clearable={false}
      helpProps={{
        text: copy.components.configureOrgSSO.dialog.okta.webApp.audience.help,
      }}
      label={copy.components.configureOrgSSO.dialog.okta.webApp.audience.label}
      onChange={(e) =>
        onChange({
          ...form,
          oktaAuthorizationServerAudience: e.target.value,
        })
      }
      required
      size="md"
      testId={CONFIGURE_ORG_SSO_DIALOG_OKTA_AUDIENCE}
      value={form.oktaAuthorizationServerAudience}
    />
    <Input
      className="organization-dialog-input-help-icon-hidden"
      clearable={false}
      helpProps={{
        text: copy.components.configureOrgSSO.dialog.okta.webApp.clientId.help,
      }}
      label={copy.components.configureOrgSSO.dialog.okta.webApp.clientId.label}
      onChange={(e) => onChange({ ...form, clientId: e.target.value })}
      required
      size="md"
      testId={CONFIGURE_ORG_SSO_DIALOG_CLIENT_ID}
      value={form.clientId}
    />
    <SecretInput
      cancelText={copy.components.configureOrgSSO.dialog.secretInput.cancel}
      className="organization-dialog-input-help-icon-hidden"
      configuredText={
        copy.components.configureOrgSSO.dialog.secretInput.configured
      }
      hasExistingValue={!isCreate}
      helpText={copy.components.configureOrgSSO.dialog.secretInput.help}
      initialValue={form.clientSecret}
      inputTestId={CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET}
      label={
        copy.components.configureOrgSSO.dialog.okta.webApp.clientSecret.label
      }
      onReplace={(clientSecret) => onChange({ ...form, clientSecret })}
      replaceTestId={CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET_REPLACE}
      replaceText={copy.components.configureOrgSSO.dialog.secretInput.replace}
      required={isCreate}
      viewTestId={CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET_VIEW}
    />
    <div className="organization-dialog-divider" />
    <div className="organization-dialog-container">
      <Body className="organization-dialog-container-header" size="lg">
        {copy.components.configureOrgSSO.dialog.okta.apiServicesApp.heading}
      </Body>
      <Body className="organization-dialog-container-header" size="md">
        {copy.components.configureOrgSSO.dialog.okta.apiServicesApp.description}
      </Body>
    </div>
    <Input
      className="organization-dialog-input-help-icon-hidden"
      clearable={false}
      label={
        copy.components.configureOrgSSO.dialog.okta.apiServicesApp.clientId
          .label
      }
      onChange={(e) =>
        onChange({ ...form, oktaBackendClientId: e.target.value })
      }
      required
      size="md"
      testId={CONFIGURE_ORG_SSO_DIALOG_OKTA_BACKEND_CLIENT_ID}
      value={form.oktaBackendClientId}
    />
    <SecretInput
      cancelText={copy.components.configureOrgSSO.dialog.secretInput.cancel}
      className="organization-dialog-input-help-icon-hidden"
      configuredText={
        copy.components.configureOrgSSO.dialog.secretInput.configured
      }
      hasExistingValue={!isCreate}
      helpText={copy.components.configureOrgSSO.dialog.secretInput.help}
      initialValue={form.oktaBackendClientSecret}
      inputTestId={CONFIGURE_ORG_SSO_DIALOG_OKTA_BACKEND_CLIENT_SECRET}
      label={
        copy.components.configureOrgSSO.dialog.okta.apiServicesApp.clientSecret
          .label
      }
      onReplace={(oktaBackendClientSecret) =>
        onChange({ ...form, oktaBackendClientSecret })
      }
      replaceTestId={
        CONFIGURE_ORG_SSO_DIALOG_OKTA_BACKEND_CLIENT_SECRET_REPLACE
      }
      replaceText={copy.components.configureOrgSSO.dialog.secretInput.replace}
      required={isCreate}
      viewTestId={CONFIGURE_ORG_SSO_DIALOG_OKTA_BACKEND_CLIENT_SECRET_VIEW}
    />
    <div className="organization-dialog-divider" />
    <InputWrapper
      className="organization-dialog-input-help-icon-hidden"
      classNamePrefix="vb-input"
      helpProps={{
        text: copy.components.configureOrgSSO.dialog.okta.idpLogout.help,
      }}
      label=""
      labelSize="md"
      size="md"
    >
      <Checkbox
        checked={form.idpLogoutEnabled}
        onChange={() =>
          onChange({ ...form, idpLogoutEnabled: !form.idpLogoutEnabled })
        }
        size="md"
        testId={CONFIGURE_ORG_SSO_DIALOG_IDP_LOGOUT}
      >
        {copy.components.configureOrgSSO.dialog.okta.idpLogout.label}
      </Checkbox>
    </InputWrapper>
  </>
);
